// ============================================================
// dev-core-web/src/store/uiStore.ts
// Version: 1.1.0
// Purpose:
//   - глобальное UI-состояние (тема, sidebar, тосты, layout-флаги)
//   - НЕ содержит бизнес-логики, НЕ делает RBAC/Classification решений
//   - НЕ хранит секреты (SECURITY.md §9.3)
//   - персистентные UI-настройки — через lib/storage.ts
//
// Правила:
//   - toasts — эфемерны, не персистятся
//   - theme/sidebar — персистятся, восстанавливаются при init
//   - никаких classified значений в UI-state
//   - id для toasts — стабильный (для dedup/dismiss)
//
// Ссылки:
//   - docs/SECURITY.md §9.3
//   - docs/adr/0006-field-level-masking.md §24
//   - dev-core-web/src/lib/storage.ts (StorageKeys)
// ============================================================

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  StorageKeys,
  readBoolean,
  readString,
  writeBoolean,
  writeString,
} from '@/lib/storage';

// ------------------------------------------------------------
// Theme
// ------------------------------------------------------------

export type ThemeMode = 'light' | 'dark' | 'system';

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

// ------------------------------------------------------------
// Toasts
// ------------------------------------------------------------
//
// Toast — эфемерное уведомление. Живёт в памяти до dismiss
// или авто-удаления через ttlMs.
//
// ВАЖНО: toast НЕ является источником истории. Для реальных
// уведомлений с сервера используется NotificationQueue из
// lib/notifications.ts. Toast — короткое подтверждение UX.

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string | undefined;
  // TTL в мс; при истечении toast удаляется автоматически.
  // 0 — не удалять автоматически.
  ttlMs: number;
  createdAt: number;
  // Опциональные действия (например, «Отменить»).
  action?: {
    label: string;
    // Callback хранится в памяти; не сериализуется.
    onClick: () => void;
  } | undefined;
}

export interface ShowToastInput {
  variant?: ToastVariant;
  title: string;
  description?: string | undefined;
  ttlMs?: number;
  action?: { label: string; onClick: () => void };
}

const DEFAULT_TOAST_TTL_MS = 5_000;

// ------------------------------------------------------------
// State
// ------------------------------------------------------------

export interface UiState {
  // Layout
  theme: ThemeMode;
  sidebarCollapsed: boolean;

  // Toasts
  toasts: Toast[];

  // Actions
  setTheme: (theme: ThemeMode) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  showToast: (input: ShowToastInput) => string;
  dismissToast: (id: string) => void;
  clearToasts: () => void;
}

// ------------------------------------------------------------
// Init from storage
// ------------------------------------------------------------
//
// Безопасное чтение при создании store. Если storage
// недоступен (SSR/private) — возвращает дефолты.

function readInitialTheme(): ThemeMode {
  const v = readString(StorageKeys.THEME);
  // Дефолт — dark (UI_STYLE §13.1/§13.3); 'light'/'system' остаются
  // доступными как явный выбор пользователя.
  return isThemeMode(v) ? v : 'dark';
}

function readInitialSidebar(): boolean {
  const v = readBoolean(StorageKeys.SIDEBAR_COLLAPSED);
  return v ?? false;
}

// ------------------------------------------------------------
// Store
// ------------------------------------------------------------

export const useUiStore = create<UiState>()(
  subscribeWithSelector((set, get) => ({
    theme: readInitialTheme(),
    sidebarCollapsed: readInitialSidebar(),
    toasts: [],

    // ----------------------------------------------------------
    // Theme
    // ----------------------------------------------------------

    setTheme: (theme) => {
      set({ theme });
      // Персист: не критично, если запись не удалась —
      // при следующем запуске будет дефолт.
      writeString(StorageKeys.THEME, theme);
    },

    // ----------------------------------------------------------
    // Sidebar
    // ----------------------------------------------------------

    toggleSidebar: () => {
      const next = !get().sidebarCollapsed;
      set({ sidebarCollapsed: next });
      writeBoolean(StorageKeys.SIDEBAR_COLLAPSED, next);
    },

    setSidebarCollapsed: (collapsed) => {
      set({ sidebarCollapsed: collapsed });
      writeBoolean(StorageKeys.SIDEBAR_COLLAPSED, collapsed);
    },

    // ----------------------------------------------------------
    // Toasts
    // ----------------------------------------------------------
    //
    // showToast возвращает id — вызывающий код может использовать
    // его для dismiss или update (update не реализован: тост
    // создаётся один раз и живёт до dismiss/ttl).

    showToast: (input) => {
      const id = generateToastId();
      const toast: Toast = {
        id,
        variant: input.variant ?? 'info',
        title: input.title,
        description: input.description,
        ttlMs: input.ttlMs ?? DEFAULT_TOAST_TTL_MS,
        createdAt: Date.now(),
        action: input.action,
      };

      set((state) => ({ toasts: [...state.toasts, toast] }));

      // Авто-удаление по TTL. Планируем через setTimeout,
      // не через interval — каждый toast имеет собственный TTL.
      // Таймеры не отменяем при manual dismiss: dismissToast
      // удалит по id, а setTimeout просто не найдёт — no-op.
      if (toast.ttlMs > 0) {
        setTimeout(() => {
          // Проверяем, что toast ещё существует — иначе no-op.
          get().dismissToast(id);
        }, toast.ttlMs);
      }

      return id;
    },

    dismissToast: (id) => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    },

    clearToasts: () => {
      set({ toasts: [] });
    },
  })),
);

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------

function generateToastId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `toast_${ts}${rand}`;
}

// ------------------------------------------------------------
// Selectors (для точечной подписки)
// ------------------------------------------------------------

export const selectTheme = (s: UiState): ThemeMode => s.theme;
export const selectSidebarCollapsed = (s: UiState): boolean => s.sidebarCollapsed;
export const selectToasts = (s: UiState): Toast[] => s.toasts;